import { utils } from 'frontend-js-shared'

import { ITEM_TYPES } from '@/components/Checklists/ChecklistCreation/constants'

import {
  createDefaultScoringSection,
  ensureWeightForSingleSection,
  isNumberQuestion,
  isOptionQuestion,
  isScoringEligibleQuestion,
  resolveScoringItemId,
  isScaleQuestion,
  isTextLikeQuestion
} from './scoringShared'
import { toNullableNumber } from '../utils'

const DEFAULT_SECTION_NAME_KEY = 'routines.scoring.default_section_name'

function ensureSectionSeparatorForScoring(
  items = [],
  defaultSectionName = DEFAULT_SECTION_NAME_KEY
) {
  const checklistItems = Array.isArray(items) ? [...items] : []
  const firstSeparatorIndex = checklistItems.findIndex(
    item => item?.type === ITEM_TYPES.SEPARATOR
  )
  const hasAnySeparator = firstSeparatorIndex !== -1

  if (firstSeparatorIndex === 0) {
    return { items: checklistItems }
  }

  return {
    items: [
      createDefaultScoringSection({
        name: defaultSectionName,
        weightPercentage: hasAnySeparator ? null : 100,
      }),
      ...checklistItems,
    ],
  }
}

function buildScoringSectionsFromItems(items = []) {
  const checklistItems = ensureWeightForSingleSection(items)

  return checklistItems.reduce((sections, item, index) => {
    if (item?.type === ITEM_TYPES.SEPARATOR) {
      sections.push({
        id: resolveScoringItemId(item, `scoring-item-${index}`),
        title: item.name || '',
        isDefaultSection: Boolean(item?.isDefaultSection),
        weightPercentage: item?.scoring?.weightPercentage ?? null,
        itemIds: [],
      })
      return sections
    }

    const currentSection = sections[sections.length - 1]
    if (currentSection) {
      currentSection.itemIds.push(
        resolveScoringItemId(item, `scoring-item-${index}`)
      )
    }

    return sections
  }, [])
}

function buildSectionScoringGroupsFromItems(items = []) {
  const checklistItems = ensureWeightForSingleSection(items)
  const groups = []
  let currentGroup = null

  checklistItems.forEach((item, index) => {
    if (item?.type === ITEM_TYPES.SEPARATOR) {
      currentGroup = {
        id: resolveScoringItemId(item, `scoring-item-${index}`),
        itemId: resolveScoringItemId(item, `scoring-item-${index}`),
        title: item?.name || '',
        isDefaultSection: Boolean(item?.isDefaultSection),
        weightPercentage: item?.scoring?.weightPercentage ?? null,
        items: [],
      }
      groups.push(currentGroup)
      return
    }

    if (!currentGroup) {
      return
    }

    currentGroup.items.push({
      ...item,
      scoring: {
        enabled: true,
        ...item?.scoring,
      },
      scoringItemId: resolveScoringItemId(item, `scoring-item-${index}`),
    })
  })

  return groups
}

function hydrateOptionQuestionScoring(item) {
  const optionPointsById = new Map(
    (item?.scoring?.rule?.optionPoints || []).map(optionPoint => [
      utils.getEntityId(optionPoint.optionId),
      toNullableNumber(optionPoint.points),
    ])
  )

  return {
    ...item,
    options: (item?.options || []).map(option => ({
      ...option,
      scoringPoints: optionPointsById.has(utils.getEntityId(option))
        ? optionPointsById.get(utils.getEntityId(option))
        : null,
    })),
  }
}

function hydrateCheckboxQuestionScoring(item) {
  const requiredOptionIds = new Set(
    (item?.scoring?.rule?.requiredOptionIds || []).map(optionId =>
      utils.getEntityId(optionId)
    )
  )

  return {
    ...item,
    options: (item?.options || []).map(option => ({
      ...option,
      countsTowardScore: requiredOptionIds.has(utils.getEntityId(option)),
    })),
    scoring: {
      ...item.scoring,
      pointsIfAllSelected: toNullableNumber(
        item?.scoring?.rule?.pointsIfAllSelected
      ),
    },
  }
}

function hydrateRangeQuestionScoring(item, range) {
  return {
    ...item,
    scoring: {
      ...item.scoring,
      range,
      pointsIfWithinRange: toNullableNumber(
        item?.scoring?.rule?.pointsIfWithinRange
      ),
    },
  }
}

function hydrateTextLikeQuestionScoring(item) {
  const pointsIfAnswered = toNullableNumber(
    item?.scoring?.rule?.pointsIfAnswered
  )

  if (pointsIfAnswered === null) {
    return item
  }

  return {
    ...item,
    scoring: {
      ...item.scoring,
      pointsIfAnswered,
    },
  }
}

function hydrateChecklistItemScoringForForm(item) {
  if (!item?.scoring) {
    return item
  }

  if (isNumberQuestion(item) || isScaleQuestion(item)) {
    return hydrateRangeQuestionScoring(item, {
      min: toNullableNumber(item?.scoring?.rule?.range?.min),
      max: toNullableNumber(item?.scoring?.rule?.range?.max),
    })
  }

  if (item?.type === ITEM_TYPES.CHECKBOXES) {
    return hydrateCheckboxQuestionScoring(item)
  }

  if (isOptionQuestion(item)) {
    return hydrateOptionQuestionScoring(item)
  }

  if (isTextLikeQuestion(item)) {
    return hydrateTextLikeQuestionScoring(item)
  }

  return item
}

function hydrateChecklistScoringForForm(checklist = {}) {
  return {
    ...checklist,
    items: (checklist?.items || []).map(hydrateChecklistItemScoringForForm),
  }
}


function mergeChecklistFollowUpScoringState(
  createdChecklist = {},
  sourceChecklist = {}
) {
  const sourceItems = Array.isArray(sourceChecklist?.items)
    ? sourceChecklist.items
    : []

  return {
    ...createdChecklist,
    items: (createdChecklist?.items || []).map((createdItem, itemIndex) => {
      if (!shouldMergeQuestionScoringState(createdItem)) {
        return createdItem
      }

      const sourceItem = sourceItems[itemIndex]
      const sourceOptions = Array.isArray(sourceItem?.options)
        ? sourceItem.options
        : []

      return {
        ...createdItem,
        scoring: mergeQuestionScoringState(createdItem, sourceItem),
        options: (createdItem?.options || []).map(
          (createdOption, optionIndex) => ({
            ...createdOption,
            scoringPoints: sourceOptions[optionIndex]?.scoringPoints ?? null,
            countsTowardScore:
              sourceOptions[optionIndex]?.countsTowardScore ?? false,
          })
        ),
      }
    }),
  }
}

function shouldMergeQuestionScoringState(item) {
  return (
    isTextLikeQuestion(item) ||
    isOptionQuestion(item) ||
    item?.type === ITEM_TYPES.CHECKBOXES ||
    isNumberQuestion(item) ||
    isScaleQuestion(item)
  )
}

function mergeTextLikeQuestionScoringState(createdItem, sourceItem) {
  return {
    ...createdItem?.scoring,
    pointsIfAnswered: sourceItem?.scoring?.pointsIfAnswered ?? null,
  }
}

function mergeCheckboxQuestionScoringState(createdItem, sourceItem) {
  return {
    ...createdItem?.scoring,
    pointsIfAllSelected: sourceItem?.scoring?.pointsIfAllSelected ?? null,
  }
}

function mergeNumberQuestionScoringState(createdItem, sourceItem) {
  return {
    ...createdItem?.scoring,
    range: {
      ...createdItem?.scoring?.range,
      ...sourceItem?.scoring?.range,
    },
    pointsIfWithinRange: sourceItem?.scoring?.pointsIfWithinRange ?? null,
  }
}

function mergeScaleQuestionScoringState(createdItem, sourceItem) {
  return {
    ...createdItem?.scoring,
    range: {
      min: toNullableNumber(createdItem?.min),
      max: toNullableNumber(createdItem?.max),
      ...createdItem?.scoring?.range,
      ...sourceItem?.scoring?.range,
    },
    pointsIfWithinRange: sourceItem?.scoring?.pointsIfWithinRange ?? null,
  }
}

function mergeQuestionScoringState(createdItem, sourceItem) {
  if (isTextLikeQuestion(createdItem)) {
    return mergeTextLikeQuestionScoringState(createdItem, sourceItem)
  }

  if (createdItem?.type === ITEM_TYPES.CHECKBOXES) {
    return mergeCheckboxQuestionScoringState(createdItem, sourceItem)
  }

  if (isNumberQuestion(createdItem)) {
    return mergeNumberQuestionScoringState(createdItem, sourceItem)
  }

  if (isScaleQuestion(createdItem)) {
    return mergeScaleQuestionScoringState(createdItem, sourceItem)
  }

  return createdItem?.scoring
}

function getTextLikeQuestionMaxPoints(item) {
  return Math.max(0, Number(item?.scoring?.pointsIfAnswered) || 0)
}

function getOptionQuestionMaxPoints(item) {
  return (item?.options || []).reduce(
    (maxPoints, option) =>
      Math.max(maxPoints, Number(option?.scoringPoints) || 0),
    0
  )
}

function hasCompleteCheckboxMaxPoints(item) {
  const hasSelectedOptions = (item?.options || []).some(
    option => option?.countsTowardScore
  )
  const hasCompletePoints =
    toNullableNumber(item?.scoring?.pointsIfAllSelected) !== null

  return hasSelectedOptions && hasCompletePoints
}

function getCheckboxQuestionMaxPoints(item) {
  if (!hasCompleteCheckboxMaxPoints(item)) {
    return 0
  }

  return Math.max(0, Number(item?.scoring?.pointsIfAllSelected) || 0)
}

function getRangeQuestionMaxPoints(item) {
  return Math.max(Number(item?.scoring?.pointsIfWithinRange) || 0, 0)
}

function getQuestionMaxPoints(item) {
  if (!isScoringEligibleQuestion(item)) {
    return 0
  }

  if (isTextLikeQuestion(item)) {
    return getTextLikeQuestionMaxPoints(item)
  }

  if (isOptionQuestion(item)) {
    return getOptionQuestionMaxPoints(item)
  }

  if (isNumberQuestion(item) || isScaleQuestion(item)) {
    return getRangeQuestionMaxPoints(item)
  }

  if (item?.type === ITEM_TYPES.CHECKBOXES) {
    return getCheckboxQuestionMaxPoints(item)
  }

  return 0
}

function calculateChecklistMaxScore(items = []) {
  return (Array.isArray(items) ? items : []).reduce(
    (accumulator, item) => accumulator + getQuestionMaxPoints(item),
    0
  )
}

export {
  DEFAULT_SECTION_NAME_KEY,
  buildScoringSectionsFromItems,
  buildSectionScoringGroupsFromItems,
  calculateChecklistMaxScore,
  hydrateChecklistScoringForForm,
  mergeChecklistFollowUpScoringState,
  ensureSectionSeparatorForScoring
}
